/* eslint-disable no-useless-escape */
import { ChangeEvent, useContext, useEffect, useState } from "react";
import { ToastContext } from "../../../contexts/ToastContext/ToastContext";
import { TextInput, Button, GooglePlaceSearchComponent } from "../..";
import { formatPhoneNumber, validateAddress } from "../../../utils";
import { addOffice, listOfficeData } from "../../../redux/slices/OfficeSlice";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import { Phone_Fax_Regex, PrimaryContactEmail, colors } from "../../../utils/constants";
import { logger } from "../../../utils/logger";
import { CreateOffice } from "../../../models/create-office.model";
import AddressField, { AddressFieldValue } from "../../CustomAddress/CustomAddress";

export interface AddOfficeInitDataType {
  id: string;
  placeholder: string;
  required: boolean;
  errorMesage: string;
  errorMesage1?: string;
  label: string;
  value: string;
  isValid: boolean;
}

const log = logger.getLogger("MapService");

const addOfficeInitData: AddOfficeInitDataType[] = [
  {
    id: "officeName",
    label: "Office Name",
    placeholder: "Enter office name",
    required: true,
    errorMesage: "Please provide an office name",
    value: "",
    isValid: false,
  },
  {
    id: "address",
    label: "Address",
    placeholder: "Enter address",
    required: true,
    errorMesage: "Please provide an office address",
    errorMesage1: "A full address with street number is required",
    value: "",
    isValid: false,
  },
  {
    id: "phone",
    label: "Phone",
    placeholder: "Enter phone number",
    required: true,
    errorMesage: "Please enter a valid phone number",
    value: "",
    isValid: false,
  },
  {
    id: "fax",
    label: "Fax",
    placeholder: "Enter fax number",
    required: true,
    errorMesage: "Please enter a valid fax number",
    value: "",
    isValid: false,
  },
  {
    id: "primaryContactName",
    label: "Primary Contact Name",
    placeholder: "Enter primary contact name",
    required: true,
    errorMesage: "Please provide primary contact's name",
    value: "",
    isValid: false,
  },
  {
    id: "primaryContactEmail",
    label: "Primary Contact Email Address",
    placeholder: "Enter primary contact email",
    required: true,
    errorMesage: "Please provide primary contact's email",
    value: "",
    isValid: false,
  },
];

const AddOffice = ({
  onCancelClick,
  setIsLoading,
  isLoading,
}: {
  onCancelClick: () => void;
  setIsLoading: (val: boolean) => void;
  isLoading?: boolean;
}) => {
  const dispatch: AppDispatch = useDispatch();
  const [officeFormData, setOfficeFormData] = useState<AddOfficeInitDataType[]>(addOfficeInitData);
  const [errorInitiated, setErrorInitiated] = useState(false);
  const [errorMsgVisible, setErrorMsgVisible] = useState<boolean>(false);
  const [places, setPlaces] = useState("");
  const [addressValidate, setAddressValidate] = useState(false);
  const toast = useContext(ToastContext);
  const officeState = useSelector((state: RootState) => state.office);
  const { sort } = officeState;
  const [customAddress, setCustomAddress] = useState<AddressFieldValue>({
    value: "",
    isValid: false,
    components: [],
  });
  const [useCustomAddress, setUseCustomAddress] = useState(false);

  useEffect(() => {
    if (errorInitiated && !officeState.apiSucess) {
      setErrorMsgVisible(true);
    } else {
      setErrorMsgVisible(false);
    }
  }, [officeState]);

  const validateField = (name: string, value: string) => {
    let valid = false;

    switch (name) {
      case "fax":
        valid = Phone_Fax_Regex.test(value);
        break;

      case "phone":
        valid = Phone_Fax_Regex.test(value);
        break;

      case "primaryContactEmail":
        valid = PrimaryContactEmail.test(value);
        break;

      case "primaryContactName":
        valid = !!value.trim();
        break;

      case "officeName":
        valid = !!value.trim();
        break;

      default:
        break;
    }
    return valid;
  };

  const handleBlur = (id: string, value: string) => {
    const index = officeFormData.findIndex(item => item.id === id);
    if (index !== -1) {
      setOfficeFormData([
        ...officeFormData.slice(0, index),
        {
          ...officeFormData[index],
          value,
        },
        ...officeFormData.slice(index + 1),
      ]);
    }
  };

  const handleFieldChange = (e: ChangeEvent<HTMLInputElement>, index: number) => {
    const phoneNumLikeFlds = ["phone", "fax"];
    let value = e.currentTarget.value;
    if (phoneNumLikeFlds.includes(e.currentTarget.name)) {
      value = formatPhoneNumber(e.currentTarget.value);
    }

    const valid = validateField(e.currentTarget.name, value);
    setOfficeFormData([
      ...officeFormData.slice(0, index),
      {
        ...officeFormData[index],
        value,
        isValid: valid,
      },
      ...officeFormData.slice(index + 1),
    ]);
  };

  const disabledState = officeFormData.some(item => item.isValid === false);
  const getAddOfficeData = () => {
    return ["officeName", "address", "phone", "fax", "primaryContactEmail", "primaryContactName"]
      .map(key => {
        const foundVal = officeFormData?.find(el => el.id === key)?.value;
        return {
          [key]: foundVal ? foundVal : "",
        };
      })
      .reduce((acc, el) => {
        return { ...acc, ...el };
      }, {});
  };

  const onClickSave = () => {
    const formInvalid = officeFormData.some(f => !f.isValid);
    const addrInvalid = !customAddress.isValid;
    if (formInvalid || addrInvalid) {
      setErrorInitiated(true);
      setAddressValidate(true);
      return;
    }
    const addressValidation = validateAddress(customAddress.components);
    if (!addressValidation) {
      setAddressValidate(true);
      return;
    }
    setIsLoading(true);
    const { officeName, phone, fax, primaryContactEmail, primaryContactName } = getAddOfficeData();
    const dataToSend: CreateOffice = {
      name: officeName,
      street: addressValidation.streetNumber,
      street1: addressValidation.street,
      street2: addressValidation.unit,
      customOfficeAddress: useCustomAddress,
      city: addressValidation.city,
      state: addressValidation.state,
      zip: addressValidation.zip,
      country: addressValidation.country,
      phone,
      fax,
      contactName: primaryContactName,
      contactEmail: primaryContactEmail,
    };

    setErrorInitiated(false);
    dispatch(addOffice(dataToSend))
      .then(action => {
        if (action.payload) {
          dispatch(
            listOfficeData({
              page: sort.pageNumber,
              size: sort.rowsPerPage,
              sortType: sort.sortedBy,
            })
          );
          onCancelClick();
          toast?.openToast("Successfully added office!");
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const onClickCancel = () => {
    onCancelClick();
  };

  const onAddrChange = (newVal: AddressFieldValue) => {
    setCustomAddress(newVal);
    setAddressValidate(!newVal.isValid);

    setOfficeFormData(prev => {
      const idx = prev.findIndex(item => item.id === "address");
      if (idx === -1) return prev;
      const next = [...prev];
      next[idx] = {
        ...next[idx],
        value: newVal.value,
        isValid: newVal.isValid,
      };
      return next;
    });
  };

  return (
    <div className="inline-block h-scrollable-screen overflow-auto">
      <p className="text-base px-8 py-5 font-medium">To add a new office, complete the information below.</p>
      {errorMsgVisible ? (
        <p className="text-center" style={{ color: colors.Red }}>
          An error has occurred adding this office
        </p>
      ) : null}
      {officeFormData.map((item: AddOfficeInitDataType, index) => {
        if (item.id === "address") {
          return (
            <div className="px-8 py-2" key={item.id}>
              <AddressField
                label={item.label}
                required={item.required}
                hideGooglePlaceSearch={false}
                disabled={false}
                error={!customAddress.isValid && errorInitiated}
                errorMsg={addressValidate ? item.errorMesage1 : item.errorMesage}
                onModeChange={isManual => {
                  setUseCustomAddress(isManual);
                }}
                onChange={onAddrChange}
                addressType=""
                darkBg={true}
              />
            </div>
          );
        }
        if (item.id === "primaryContactEmail") {
          return (
            <div className="px-8 py-2" key={item.id}>
              <TextInput
                isError={!item.isValid && errorInitiated}
                value={item.value}
                name={item.id}
                onChangeFunc={e => handleFieldChange(e, index)}
                label={item.label}
                errorMsg={item.value === "" ? "Please provide primary contact's email" : "Please provide a valid email address"}
                isRequired={item.required}
                placeholder={item.placeholder}
                onBlur={e => handleBlur(item.id, e.target.value.trim())}
                isDarkBg={true}
              />
            </div>
          );
        }
        if (item.id === "phone" || item.id === "fax") {
          return (
            <div key={item.id} className="px-8 py-2">
              <TextInput
                isError={!item.isValid && errorInitiated}
                value={item.value}
                name={item.id}
                onChangeFunc={e => handleFieldChange(e, index)}
                label={item.label}
                errorMsg={item.errorMesage}
                isRequired={item.required}
                placeholder={item.placeholder}
                isDarkBg={true}
              />
            </div>
          );
        } else {
          return (
            <div key={item.id} className="px-8 py-2">
              <TextInput
                isError={!item.isValid && errorInitiated}
                value={item.value}
                name={item.id}
                onChangeFunc={e => handleFieldChange(e, index)}
                label={item.label}
                errorMsg={item.errorMesage}
                isRequired={item.required}
                placeholder={item.placeholder}
                onBlur={e => handleBlur(item.id, e.target.value.trim())}
                isDarkBg={true}
              />
            </div>
          );
        }
      })}

      <div className="fixed bottom-0 right-0 w-11/12 max-w-120 bg-docsigna-pink-light">
        <div className="px-8 py-5 flex justify-end">
          <Button onClick={() => onClickCancel()} className="pointer px-5 mr-3" text="Cancel" width="fit" variant="Secondary" disabled={isLoading} />
          <div>
            <Button
              onClick={onClickSave}
              disabled={disabledState || !customAddress.isValid || isLoading}
              className="pointer px-5"
              text="Save"
              width="fit"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
export default AddOffice;
